import { inject, observer } from 'mobx-react'
import React from 'react'
import type { NextPageContext } from 'next'

import { getHomeData } from '../../../utils/nextUtils'
import type { LanguageCode } from 'utils/utils'
import { getLocaleStr, codeToLocale, safeStringify } from 'utils/utils'
import { CACHE_POLICY } from '../../../utils/constants'

import StoreLocator from '../../../src/static/pages/StoreLocator'
import CustomHead from '../../../components/CustomHead'

import type _User from 'mobx/User'
import type _Infra from 'mobx/Infra'

interface StoreLocatorBaseProps {
	chainId: string
	lang: string
	conversationText: any
	footer: any
	isGeneratedStatically: boolean
	params: any
	chainCountryLocation: {
		latLng: {
			lat: number
			lng: number
		}
	}
}

interface Props {
	lang: LanguageCode
	data: {
		chainStores: { error: boolean; msg: any }
	}
}

const StoreLocatorPage = inject(
	'Infra',
	'User'
)(
	observer(({ Infra, User, lang, data }: Props & { Infra: typeof _Infra; User: typeof _User }) => {
		const storeLocatorPageText = Infra.appParams?.pageText['store-locator']
		const title = getLocaleStr(storeLocatorPageText?.title, codeToLocale[lang] || 'en_US')
		const description = getLocaleStr(storeLocatorPageText?.description, codeToLocale[lang] || 'en_US')
		const storeLocatorBaseProps: StoreLocatorBaseProps = {
			chainId: Infra.appParams.c,
			lang: User.preferredLanguage || Infra.appParams.l,
			conversationText: User.userLocaleLanguage,
			footer: Infra.eCommerceFooter,
			isGeneratedStatically: true,
			params: Infra.appParams,
			// TODO FOR NEXT: the prop 'chainCountryLocation' is not used but it's required by the StoreLocator & Map components
			// reason: Map component use the 'center' prop to center the map on a specific bound (related to all stores)
			// easy fix: move default value bellow directly to Map component
			chainCountryLocation: {
				latLng: {
					lat: data?.chainStores?.msg[0]?.address?.latLng?.lat || 51.118957722436434,
					lng: data?.chainStores?.msg[0]?.address?.latLng?.lng || 10.460889308117967,
				},
			},
		}

		// Remove 'ref' from appParams because it throws an error on CustomHead (string ref prop not allowed on function component, see https://legacy.reactjs.org/docs/refs-and-the-dom.html#legacy-api-string-refs)
		const { ref, ...appParamsWithoutRef } = Infra.appParams

		return (
			<>
				<CustomHead {...appParamsWithoutRef} title={title} description={description} lang={lang} key={1} />
				<StoreLocator {...storeLocatorBaseProps} chainStores={data.chainStores} key={2} />
			</>
		)
	}) as React.FC<Props>
)

export async function getServerSideProps(
	context: NextPageContext & { req: { extra: { appParams: any; nextJsError?: string; forwardedUrl: string } } }
) {
	const lang = context.query.lang as string
	const { req, res } = context

	if (req && !req.extra?.nextJsError) {
		try {
			const appParams = context.req.extra?.appParams
			const chainStores = await getHomeData(appParams, req.extra.forwardedUrl, 'getBranchesList', true)

			if (res) {
				res.setHeader(
					'Cache-Control',
					`public, s-maxage=${CACHE_POLICY.DEFAULT.MAX_AGE}, stale-while-revalidate=${CACHE_POLICY.DEFAULT.REVALIDATED_AGE}`
				)
			}

			return {
				props: {
					data: {
						chainStores,
					},
					lang,
				},
			}
		} catch (e: any) {
			console.error(safeStringify(e))
		}
	}

	if (context.res) {
		context.res.statusCode = 500
		context.res.setHeader('Cache-Control', 'no-store')
	}

	return { props: { data: null } }
}

export default StoreLocatorPage as React.FC
